import React, { useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import { Select, DatePicker, Alert, Skeleton } from "antd";
import CurrencySummaryCards from "../components/analytics/CurrencySummaryCards";
import AverageStatsCards from "../components/analytics/AverageStatsCards";
import MonthlySpendingChart from "../components/analytics/MonthlySpendingChart";
import MonthlyCategoryChart from "../components/analytics/MonthlyCategoryChart";
import CategoryTrendChart from "../components/analytics/CategoryTrendChart";
import CategoryBreakdownChart from "../components/analytics/CategoryBreakdownChart";
import NoteSpendingChart from "../components/analytics/NoteSpendingChart";
import TopSpendingsList from "../components/analytics/TopSpendingsList";
import SpendingDetailModal from "../components/analytics/SpendingDetailModal";
import { Card, CardTitle } from "../components/analytics/layout";
import useAnalytics from "../hooks/useAnalytics";

const Wrapper = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 1.5em 1em 4em;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1em;
  margin-bottom: 1.25em;
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: 20px;
`;

const Section = styled.div`
  margin-bottom: 1.25em;
`;

const { RangePicker } = DatePicker;

const CUSTOM_RANGE = "custom";

const MONTH_OPTIONS = [
  { value: 3, label: "近 3 個月" },
  { value: 6, label: "近 6 個月" },
  { value: 12, label: "近 12 個月" },
  { value: CUSTOM_RANGE, label: "自訂範圍" },
];

function disableFutureDate(current) {
  return current && current > dayjs().endOf("day");
}

export default function Analytics() {
  const [rangeSelection, setRangeSelection] = useState(6);
  const [customRange, setCustomRange] = useState(null);
  const [drillDown, setDrillDown] = useState(null);

  const isCustomRange = rangeSelection === CUSTOM_RANGE;
  const hasCustomRange = isCustomRange && Boolean(customRange);
  const startDate = hasCustomRange ? customRange[0].format("YYYY-MM-DD") : undefined;
  const endDate = hasCustomRange ? customRange[1].format("YYYY-MM-DD") : undefined;

  const {
    error,
    monthlySpending,
    categorySpending,
    monthlyCategorySpending,
    noteSpending,
    topSpendings,
    currencySummary,
  } = useAnalytics(isCustomRange ? { startDate, endDate } : { months: rangeSelection });

  const selectedLabel = isCustomRange
    ? hasCustomRange &&
      `${customRange[0].format("YYYY/M/D")} ~ ${customRange[1].format("YYYY/M/D")}`
    : MONTH_OPTIONS.find((option) => option.value === rangeSelection).label;
  const showError = error && !monthlySpending;
  const awaitingCustomRange = isCustomRange && !hasCustomRange;

  return (
    <Wrapper>
      <PageHeader>
        <PageTitle>收支分析</PageTitle>
        <div style={{ display: "flex", gap: "0.5em" }}>
          <Select value={rangeSelection} onChange={setRangeSelection} style={{ width: 130 }}>
            {MONTH_OPTIONS.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
          {isCustomRange && (
            <RangePicker
              value={customRange}
              onChange={setCustomRange}
              disabledDate={disableFutureDate}
              allowClear
            />
          )}
        </div>
      </PageHeader>

      {showError && (
        <Alert
          type="error"
          showIcon
          message="分析資料載入失敗"
          description="請稍後重新整理再試一次。"
          style={{ marginBottom: "1.5em" }}
        />
      )}

      {!showError && awaitingCustomRange && (
        <Alert type="info" showIcon message="請選擇起訖日期" style={{ marginBottom: "1.5em" }} />
      )}

      {!showError && !awaitingCustomRange && (
        <>
          <Section>
            {currencySummary ? (
              <CurrencySummaryCards currencySummary={currencySummary} />
            ) : (
              <Card>
                <Skeleton active title={false} paragraph={{ rows: 2 }} />
              </Card>
            )}
          </Section>

          <Section>
            <Card>
              <CardTitle>平均統計（{selectedLabel}）</CardTitle>
              {noteSpending ? (
                <AverageStatsCards
                  noteSpending={noteSpending}
                  months={isCustomRange ? undefined : rangeSelection}
                  startDate={startDate}
                  endDate={endDate}
                />
              ) : (
                <Skeleton active title={false} paragraph={{ rows: 2 }} />
              )}
            </Card>
          </Section>

          <Section>
            <Card>
              <CardTitle>每月收支（{selectedLabel}）</CardTitle>
              {monthlySpending ? (
                <MonthlySpendingChart
                  monthlySpending={monthlySpending}
                  months={isCustomRange ? undefined : rangeSelection}
                  startDate={startDate}
                  endDate={endDate}
                />
              ) : (
                <Skeleton active title={false} paragraph={{ rows: 5 }} />
              )}
            </Card>
          </Section>

          <Section>
            <Card>
              <CardTitle>每月分類收支（{selectedLabel}）</CardTitle>
              {monthlyCategorySpending ? (
                <MonthlyCategoryChart
                  monthlyCategorySpending={monthlyCategorySpending}
                  months={isCustomRange ? undefined : rangeSelection}
                  startDate={startDate}
                  endDate={endDate}
                />
              ) : (
                <Skeleton active title={false} paragraph={{ rows: 5 }} />
              )}
            </Card>
          </Section>

          <Section>
            <Card>
              <CardTitle>分類趨勢（{selectedLabel}）</CardTitle>
              {categorySpending ? (
                <CategoryTrendChart
                  categorySpending={categorySpending}
                  months={isCustomRange ? undefined : rangeSelection}
                  startDate={startDate}
                  endDate={endDate}
                  onSelect={setDrillDown}
                />
              ) : (
                <Skeleton active title={false} paragraph={{ rows: 4 }} />
              )}
            </Card>
          </Section>

          <Section>
            <Card>
              <CardTitle>收支細項分布（{selectedLabel}）</CardTitle>
              {categorySpending ? (
                <CategoryBreakdownChart
                  categorySpending={categorySpending}
                  onSelect={setDrillDown}
                />
              ) : (
                <Skeleton active title={false} paragraph={{ rows: 4 }} />
              )}
            </Card>
          </Section>

          <Section>
            <Card>
              <CardTitle>消費明細排行（{selectedLabel}）</CardTitle>
              {noteSpending ? (
                <NoteSpendingChart noteSpending={noteSpending} onSelect={setDrillDown} />
              ) : (
                <Skeleton active title={false} paragraph={{ rows: 4 }} />
              )}
            </Card>
          </Section>

          <Section>
            <Card>
              <CardTitle>單筆最高消費（{selectedLabel}）</CardTitle>
              {topSpendings ? (
                <TopSpendingsList topSpendings={topSpendings} />
              ) : (
                <Skeleton active title={false} paragraph={{ rows: 4 }} />
              )}
            </Card>
          </Section>
        </>
      )}

      <SpendingDetailModal
        filter={drillDown}
        months={isCustomRange ? undefined : rangeSelection}
        startDate={startDate}
        endDate={endDate}
        rangeLabel={selectedLabel}
        onClose={() => setDrillDown(null)}
      />
    </Wrapper>
  );
}
